import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { signOut, useSession as useNextAuthSession } from 'next-auth/react';
// Note: PHP RPC imports removed - now using Next.js API endpoints
import { LS_Get, LS_Set, LS_Delete } from '../utils/local-storage';
import { trackError, ErrorSeverity } from '@/utils/error-tracking';
import { UpdateProfileResponseSchema, type UpdateProfileResponse } from '@/schemas/api/user/profile';
import {
  NonJsonResponseError,
  isEdgeRefusal,
  parseJsonResponse,
} from '../utils/read-json-response';
import {
  probeOwnOrigin,
  severityForOwnFetchFailure,
  shouldRetryTransportFailure,
  transportFailureContext,
} from '../utils/fetch-transport-failure';
import { googleEvent, createNotification } from '../utils/dom';
import { getAnalytics } from '../utils/analytics';
import { nativeFlag } from '../utils/native/stripe-connect-browser';
import { hardReplace } from '../utils/hard-navigate';
import {
  ConnectCreateRequestSchema,
  ConnectCreateResponseSchema,
  type ConnectCreateRequest,
  type ConnectCreateResponse
} from '@/schemas/api/wallet/connect';
import {
  WishlistActionResponseSchema,
  WishlistGetResponseSchema,
  DeactivateUserResponseSchema,
  CloseCodeResponseSchema,
  NextAuthSessionResponseSchema,
  type WishlistItem,
} from '@/schemas/api/user';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  trustLevel: number;
  createdDate?: string;
  newMessagesNum?: number;
  photos?: {
    thumbUrl: string;
    url?: string;
  };
  phone?: string;
  username?: string;
}

// Default profile for SSR
const DEFAULT_PROFILE: UserProfile = {
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  trustLevel: 0,
  newMessagesNum: 0,
  photos: {
    thumbUrl: '',
    url: ''
  },
  phone: '',
  username: ''
};

/** GET /api/user/profile's profile: the database row, not the session copy. */
export type StoredProfile = Extract<UpdateProfileResponse, { success: true }>['profile'];

export interface UserContextType {
  profile: UserProfile | null;
  loggedIn: boolean;
  isLoading: boolean;
  watchedListings: WishlistItem[];
  fetchUserProfile: (forceRefresh?: boolean) => Promise<UserProfile | null>;
  /**
   * The signed-in user's profile as the DATABASE holds it, or null if it cannot
   * be read. A form that saves a profile must load from this, not from
   * fetchUserProfile: the session copy can be stale or carry no name.
   */
  fetchStoredProfile: () => Promise<StoredProfile | null>;
  logout: () => Promise<void>;
  setProfile: (profile: UserProfile) => void;
  setLoggedIn: (loggedIn: boolean) => void;
  watchListing: (product: WishlistItem) => Promise<boolean>;
  unwatchListing: (listingId: string) => Promise<boolean>;
  getWatches: () => Promise<WishlistItem[]>;
  /**
   * Close My Account step 1: email (or text) a code to the account's own stored
   * contact. Returns where it went, masked, or the server's sentence.
   */
  sendCloseCode: () => Promise<{ success: boolean; sentTo?: string; error?: string }>;
  /** Close My Account step 2: a soft close, with the code. The error is the server's sentence. */
  deactivateUser: (code: string) => Promise<{ success: boolean; error?: string }>;
  /** Delete My Account. On refusal, `error` is the sentence to show. */
  autoLogin: () => Promise<void>;
  createStripeConnectLink: (countryId?: number) => Promise<ConnectCreateResponse>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Use NextAuth's session hook - this eliminates redundant session fetches
  const { data: nextAuthSession, status: nextAuthStatus } = useNextAuthSession();

  // Use default profile during SSR to prevent null errors
  const [profile, setProfileState] = useState<UserProfile | null>(
    typeof window === 'undefined' ? DEFAULT_PROFILE : null
  );

  const setProfile = (newProfile: UserProfile | null) => {
    setProfileState(newProfile);
  };

  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start as loading
  const [watchedListings, setWatchedListings] = useState<WishlistItem[]>([]);
  const wishlistFetchedRef = useRef(false);

  // Refs for stable callback access (avoids re-creating callbacks on every state change)
  const profileRef = useRef(profile);
  const loggedInRef = useRef(loggedIn);
  const watchedListingsRef = useRef(watchedListings);

  // Keep refs in sync with the latest state. These refs are only read later in
  // stable callbacks/effects, so updating them after commit is behavior-preserving.
  useEffect(() => {
    profileRef.current = profile;
    loggedInRef.current = loggedIn;
    watchedListingsRef.current = watchedListings;
  }, [profile, loggedIn, watchedListings]);

  // Sync with NextAuth session - replaces initAuth
  useEffect(() => {
    // Check if we just logged out - prevent immediate re-login
    const justLoggedOut = sessionStorage.getItem('justLoggedOut');
    if (justLoggedOut === 'true') {
      sessionStorage.removeItem('justLoggedOut');
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing auth state from the external NextAuth session + sessionStorage flag; cannot be derived during render
      setLoggedIn(false);
      setProfile(null);
      setIsLoading(false);
      return;
    }

    if (nextAuthStatus === 'loading') {
      // Still loading session from NextAuth
      setIsLoading(true);
      return;
    }

    if (nextAuthStatus === 'unauthenticated' || !nextAuthSession?.user) {
      setLoggedIn(false);
      setProfile(null);
      setIsLoading(false);
      wishlistFetchedRef.current = false;
      return;
    }

    // User is authenticated - sync profile data
    const userProfile = nextAuthSession.profile || {
      id: nextAuthSession.user?.id || '',
      email: nextAuthSession.user?.email || '',
      firstName: '',
      lastName: '',
      trustLevel: 0,
      username: nextAuthSession.user?.name || '',
    };

    setProfile(userProfile);
    setLoggedIn(true);

    // Load wishlist once per session
    if (!wishlistFetchedRef.current) {
      const cachedWishlist = LS_Get('userWishlist');
      if (cachedWishlist && Array.isArray(cachedWishlist)) {
        setWatchedListings(cachedWishlist);
        wishlistFetchedRef.current = true;
      } else {
        // Fetch wishlist data from API if not cached
        void (async () => {
          try {
            const watchesResponse = await fetch('/api/user/wishlist', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                action: 'get',
              }),
            });
            const rawWatchesData = await watchesResponse.json();

            // Validate response with Zod schema
            const parseResult = WishlistGetResponseSchema.safeParse(rawWatchesData);
            if (!parseResult.success) {
              trackError(parseResult.error.flatten() as unknown as Error, { action: 'Initial wishlist fetch validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
              return;
            }

            const watchesData = parseResult.data;
            if (watchesData.success && 'watches' in watchesData) {
              const watches = watchesData.watches.reverse();
              setWatchedListings(watches);
              LS_Set('userWishlist', watches);
              wishlistFetchedRef.current = true;
            }
          } catch (error) {
            trackError(error, { action: 'Error loading watches', component: 'UserProvider' } as unknown as ErrorSeverity);
          }
        })();
      }
    }

    setIsLoading(false);
  }, [nextAuthSession, nextAuthStatus]); // Re-run when NextAuth session changes

  const fetchStoredProfile = useCallback(async (): Promise<StoredProfile | null> => {
    try {
      const response = await fetch('/api/user/profile');
      const parsed = UpdateProfileResponseSchema.safeParse(await response.json());
      if (parsed.success && parsed.data.success) return parsed.data.profile;
    } catch (error) {
      trackError(error, { action: 'fetchStoredProfile', component: 'UserProvider' } as unknown as ErrorSeverity);
    }
    return null;
  }, []);

  const fetchUserProfile = useCallback(async (forceRefresh = false): Promise<UserProfile | null> => {
    // If we already have a profile and are logged in, return it (unless force refresh)
    if (!forceRefresh && profileRef.current && loggedInRef.current) {
      return profileRef.current;
    }

    let retried = false;
    try {
      // Fetch profile from NextAuth session (not PHP API).
      //
      // Prod 2026-10-28 18:24Z: an iPhone Safari visitor on the home page had
      // this request cut three times ("Load failed", no stack, no status), and
      // the row could not say whether the phone was offline, the page leaving,
      // or our route unreachable. A cut on a live page gets one more try,
      // since a dropped session check reads as signed out.
      let response: Response;
      try {
        response = await fetch('/api/auth/session');
      } catch (firstCut) {
        if (!shouldRetryTransportFailure(firstCut)) throw firstCut;
        retried = true;
        response = await fetch('/api/auth/session');
      }
      // `await response.json()` on a body the edge wrote throws a SyntaxError
      // whose message is the first ten characters of that body and nothing
      // else. On 2026-09-17 that reached the table as "Unexpected end of JSON
      // input" from /account/profile -- an empty-bodied refusal, with no
      // status, no endpoint and no way to tell a WAF block from a crashed
      // function. This route runs on every page for every visitor, so it is
      // the one that most needed to say which.
      const rawSession = await parseJsonResponse<unknown>(response, 'GET /api/auth/session');

      // Validate session response with Zod schema
      const parseResult = NextAuthSessionResponseSchema.safeParse(rawSession);
      if (!parseResult.success) {
        trackError(parseResult.error.flatten() as unknown as Error, { action: 'fetchUserProfile validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
        return null;
      }

      const session = parseResult.data;

      if (session?.profile) {
        // Full profile is stored in session
        const userProfile: UserProfile = {
          id: String(session.profile.id),
          email: session.profile.email || '',
          firstName: session.profile.firstName || '',
          lastName: session.profile.lastName || '',
          trustLevel: session.profile.trustLevel || 0,
          createdDate: session.profile.createdDate || undefined,
          newMessagesNum: session.profile.newMessagesNum || undefined,
          photos: (session.profile.photos as UserProfile['photos']) || undefined,
          phone: session.profile.phone || undefined,
          username: session.profile.username || undefined,
        };
        setProfile(userProfile);
        setLoggedIn(true);
        return userProfile;
      } else if (session?.user) {
        // Fallback to basic user data if full profile not available
        const basicProfile: UserProfile = {
          id: session.user.id || '',
          email: session.user.email || '',
          firstName: '',
          lastName: '',
          trustLevel: 0,
          username: session.user.name || undefined,
        };

        setProfile(basicProfile);
        setLoggedIn(true);
        return basicProfile;
      }

      // No session found - user not logged in
      return null;
    } catch (error) {
      // Every route on this app answers in JSON, so a non-JSON 401/403 was
      // written by the edge before our code ran: a refused visitor (the first
      // one seen was bingbot), not a fault here. It still gets a row -- a WAF
      // rule that starts refusing the session endpoint would sign everybody
      // out -- but at warning, and carrying the status and the body prefix the
      // message alone drops.
      //
      // A request that never got an answer instead carries the browser state
      // at the catch, whether it was retried, and -- cut twice -- whether our
      // origin answers at all. Cut while offline or leaving, it files at info.
      const transport = transportFailureContext(error);
      const originReachable = retried && transport.transportFailure ? await probeOwnOrigin() : null;
      const fallback = isEdgeRefusal(error) ? ErrorSeverity.WARNING : ErrorSeverity.ERROR;
      trackError(
        error as Error,
        severityForOwnFetchFailure(error, fallback, transport),
        {
          action: 'Error fetching user profile',
          component: 'UserProvider',
          ...(error instanceof NonJsonResponseError
            ? { extra: { status: error.status, bodyPrefix: error.bodyPrefix } }
            : transport.transportFailure
              ? { extra: { request: 'GET /api/auth/session', retried, originReachable, ...transport } }
              : {}),
        }
      );
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    googleEvent('logout');

    // Clear localStorage data
    LS_Delete('postData');
    LS_Delete('userWishlist');

    // Clear state
    setProfile(null);
    setLoggedIn(false);
    setWatchedListings([]);

    // Clear NextAuth session
    try {
      await signOut({
        redirect: false,
        callbackUrl: '/'
      });

      // Call custom logout endpoint to clear any lingering cookies
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin'
      });
    } catch (error) {
      trackError(error, { action: 'Error during logout', component: 'UserProvider' } as unknown as ErrorSeverity);
    }

    // Shutdown Intercom if available
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('shutdown');
    }

    // Set flags for post-logout behavior.
    //
    // Guarded: storage access throws outright in Safari private mode and in
    // some embedded webviews, and these two lines sit between the sign-out and
    // the redirect. Unguarded, a browser that refuses storage left the visitor
    // signed out but still on /logout, staring at an empty page, with an
    // unhandled rejection behind it. A missing post-logout toast is the
    // cheaper loss.
    try {
      sessionStorage.setItem('justLoggedOut', 'true');
      localStorage.setItem('showLogoutNotification', 'true');
    } catch (error) {
      trackError(error, { action: 'Could not set post-logout flags', component: 'UserProvider' } as unknown as ErrorSeverity);
    }

    // Redirect to home page. logout runs from a click, so this is the browser.
    hardReplace('/');
  }, []);

  const watchListing = useCallback((product: WishlistItem) => {
    // Capture the current watchlist before optimistic update for rollback
    const previousWatchlist = watchedListingsRef.current;

    // Optimistic update: update state immediately for instant animation
    const updatedWatchlist = [...watchedListingsRef.current, product];
    setWatchedListings(updatedWatchlist);
    LS_Set('userWishlist', updatedWatchlist);

    // Track wishlist add with analytics
    const analytics = getAnalytics();
    if (analytics) {
      try {
        analytics.wishlist({
          listingId: parseInt(product.id),
          listingTitle: product.title,
          listingPrice: parseFloat(String(product.price)) || undefined,
          categoryId: product.categoryId ? parseInt(String(product.categoryId)) : undefined,
          categoryGroupId: product.categoryGroupId ? parseInt(String(product.categoryGroupId)) : undefined,
          action: 'add',
          sourcePage: 'product_page',
        });
      } catch (error) {
        trackError(error, { action: 'Error tracking wishlist add', component: 'UserProvider' } as unknown as ErrorSeverity);
      }
    }

    // Make API call asynchronously (don't await)
    fetch('/api/user/wishlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'watch',
        listingId: product.id,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .then(rawData => {
        // Validate response with Zod schema
        const parseResult = WishlistActionResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
          trackError(parseResult.error.flatten() as unknown as Error, { action: 'watchListing validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
          // Rollback on validation failure
          setWatchedListings(previousWatchlist);
          LS_Set('userWishlist', previousWatchlist);
          createNotification({
            msg: 'Failed to save to wishlist. Please try again.',
            type: 'error',
          });
          return;
        }

        const data = parseResult.data;
        if (data.success) {
          googleEvent('add_to_wishlist', {
            currency: 'USD',
            value: Number(product?.price) * 0.1,
            items: [{
              item_id: product?.id,
              item_name: product?.title,
              affiliation: product?.user?.id,
              currency: 'USD',
              index: 0,
              item_category: product?.categoryGroupId,
              item_category2: product?.categoryId,
              price: product?.price,
              quantity: 1,
            }],
          });
        } else {
          // Rollback on failure
          setWatchedListings(previousWatchlist);
          LS_Set('userWishlist', previousWatchlist);
          trackError(new Error((data as { error?: string }).error || 'Unknown error'), { action: 'Failed to watch listing', component: 'UserProvider' } as unknown as ErrorSeverity);
          createNotification({
            msg: 'Failed to save to wishlist. Please try again.',
            type: 'error',
          });
        }
      })
      .catch(error => {
        // Rollback on error
        setWatchedListings(previousWatchlist);
        LS_Set('userWishlist', previousWatchlist);
        trackError(error, { action: 'Error watching listing', component: 'UserProvider' } as unknown as ErrorSeverity);
        createNotification({
          msg: 'Failed to save to wishlist. Please try again.',
          type: 'error',
        });
      });

    return Promise.resolve(true); // Return immediately for optimistic UI
  }, []);

  const unwatchListing = useCallback((listingId: string) => {
    // Capture the current watchlist before optimistic update for rollback
    const previousWatchlist = watchedListingsRef.current;

    // Optimistic update: update state immediately for instant animation
    const removedProduct = watchedListingsRef.current.find(l => l.id === listingId);
    const updatedWatchlist = watchedListingsRef.current.filter(l => l.id !== listingId);
    setWatchedListings(updatedWatchlist);
    LS_Set('userWishlist', updatedWatchlist);

    // Track wishlist remove with analytics
    const analytics = getAnalytics();
    if (analytics && removedProduct) {
      try {
        analytics.wishlist({
          listingId: parseInt(removedProduct.id),
          listingTitle: removedProduct.title,
          listingPrice: parseFloat(String(removedProduct.price)) || undefined,
          categoryId: removedProduct.categoryId ? parseInt(String(removedProduct.categoryId)) : undefined,
          categoryGroupId: removedProduct.categoryGroupId ? parseInt(String(removedProduct.categoryGroupId)) : undefined,
          action: 'remove',
          sourcePage: 'product_page',
        });
      } catch (error) {
        trackError(error, { action: 'Error tracking wishlist remove', component: 'UserProvider' } as unknown as ErrorSeverity);
      }
    }

    // Make API call asynchronously (don't await)
    fetch('/api/user/wishlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'unwatch',
        listingId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .then(rawData => {
        // Validate response with Zod schema
        const parseResult = WishlistActionResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
          trackError(parseResult.error.flatten() as unknown as Error, { action: 'unwatchListing validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
          // Rollback on validation failure
          setWatchedListings(previousWatchlist);
          LS_Set('userWishlist', previousWatchlist);
          createNotification({
            msg: 'Failed to remove from wishlist. Please try again.',
            type: 'error',
          });
          return;
        }

        const data = parseResult.data;
        if (data.success) {
          googleEvent('unwatch_listing', listingId);
        } else {
          // Rollback on failure
          setWatchedListings(previousWatchlist);
          LS_Set('userWishlist', previousWatchlist);
          trackError(new Error((data as { error?: string }).error || 'Unknown error'), { action: 'Failed to unwatch listing', component: 'UserProvider' } as unknown as ErrorSeverity);
          createNotification({
            msg: 'Failed to remove from wishlist. Please try again.',
            type: 'error',
          });
        }
      })
      .catch(error => {
        // Rollback on error
        setWatchedListings(previousWatchlist);
        LS_Set('userWishlist', previousWatchlist);
        trackError(error, { action: 'Error unwatching listing', component: 'UserProvider' } as unknown as ErrorSeverity);
        createNotification({
          msg: 'Failed to remove from wishlist. Please try again.',
          type: 'error',
        });
      });

    return Promise.resolve(true); // Return immediately for optimistic UI
  }, []);

  const getWatches = useCallback(async () => {
    // Always fetch fresh data from API
    try {
      const response = await fetch('/api/user/wishlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get',
        }),
      });

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = WishlistGetResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        trackError(parseResult.error.flatten() as unknown as Error, { action: 'getWatches validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
        // Fall back to cached data on validation error
        const cachedWishlist = LS_Get('userWishlist');
        if (cachedWishlist && Array.isArray(cachedWishlist)) {
          setWatchedListings(cachedWishlist);
          return cachedWishlist;
        }
        return [];
      }

      const data = parseResult.data;
      if (data.success && 'watches' in data) {
        const watches = data.watches;
        setWatchedListings(watches);
        // Update cache
        LS_Set('userWishlist', watches);
        return watches;
      }
      return [];
    } catch (error) {
      trackError(error, { action: 'Error getting watches', component: 'UserProvider' } as unknown as ErrorSeverity);
      // Fall back to cached data on error
      const cachedWishlist = LS_Get('userWishlist');
      if (cachedWishlist && Array.isArray(cachedWishlist)) {
        setWatchedListings(cachedWishlist);
        return cachedWishlist;
      }
      return [];
    }
  }, []);

  const sendCloseCode = useCallback(async (): Promise<{ success: boolean; sentTo?: string; error?: string }> => {
    const fallback = 'We could not send the code. Please try again.';
    try {
      const response = await fetch('/api/user/close-code', { method: 'POST' });
      const parsed = CloseCodeResponseSchema.safeParse(await response.json());
      if (!parsed.success) {
        trackError(parsed.error.flatten() as unknown as Error, { action: 'sendCloseCode validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
        return { success: false, error: fallback };
      }
      const data = parsed.data as { success: boolean; sentTo?: string; error?: string };
      return data.success ? { success: true, sentTo: data.sentTo } : { success: false, error: data.error || fallback };
    } catch (error) {
      trackError(error, { action: 'Error sending close code', component: 'UserProvider' } as unknown as ErrorSeverity);
      return { success: false, error: fallback };
    }
  }, []);

  const deactivateUser = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    const fallback = 'Could not close your account. Please try again or contact support.';
    try {
      const response = await fetch('/api/user/deactivate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = DeactivateUserResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        trackError(parseResult.error.flatten() as unknown as Error, { action: 'deactivateUser validation failed', component: 'UserProvider' } as unknown as ErrorSeverity);
        return { success: false, error: fallback };
      }

      const data = parseResult.data as { success: boolean; error?: string };
      return data.success ? { success: true } : { success: false, error: data.error || fallback };
    } catch (error) {
      trackError(error, { action: 'Error deactivating user', component: 'UserProvider' } as unknown as ErrorSeverity);
      return { success: false, error: fallback };
    }
  }, []);

  const autoLogin = useCallback(async () => {
    const userProfile = await fetchUserProfile();
    if (!userProfile) {
      setLoggedIn(false);
      return;
    }

    setProfile(userProfile);
    setLoggedIn(true);
    googleEvent('auto_login', userProfile);
  }, [fetchUserProfile]);

  /**
   * Create Stripe Connect onboarding link with full type safety and Zod validation
   * Replaces createStripeConnectLinkNextAPI from network.ts
   */
  const createStripeConnectLink = useCallback(async (countryId?: number): Promise<ConnectCreateResponse> => {
    try {
      // From the app, the link Stripe mints returns the seller to the app
      // (via /api/wallet/stripe-return), not the website.
      const requestBody: ConnectCreateRequest = {
        ...(countryId ? { countryId } : {}),
        ...nativeFlag(),
      };

      // Validate request with Zod schema
      const validatedRequest = ConnectCreateRequestSchema.parse(requestBody);

      const response = await fetch('/api/wallet/connect/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validatedRequest),
      });

      const data = await response.json();

      // Validate response with Zod schema
      return ConnectCreateResponseSchema.parse(data);
    } catch (error) {
      trackError(error, { action: 'Error creating Stripe Connect link', component: 'UserProvider' } as unknown as ErrorSeverity);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create Stripe Connect link'
      };
    }
  }, []);

  const value: UserContextType = {
    profile,
    loggedIn,
    isLoading,
    watchedListings,
    fetchUserProfile,
    fetchStoredProfile,
    logout,
    setProfile,
    setLoggedIn,
    watchListing,
    unwatchListing,
    getWatches,
    deactivateUser,
    sendCloseCode,
    autoLogin,
    createStripeConnectLink,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserProvider;